// Keeps Tab / Shift+Tab inside an open layer (modal, side panel) and moves focus in and back out.
// esc_stack.js decides which layer is topmost and calls these; nothing here tracks layers itself.
// ENG-35498.
//
// Deliberately keydown-only, with no focusin enforcement: pickers, TinyMCE dialogs and Bootstrap
// modals append to document.body, outside the layer, and must keep focus when they take it.

const TABBABLE = [
  'a[href]',
  'area[href]',
  'button',
  'input:not([type="hidden"])',
  'select',
  'textarea',
  'iframe',
  'summary',
  '[tabindex]',
  '[contenteditable]:not([contenteditable="false"])',
].join(', ')

export function isFocusable(el) {
  if (!el?.isConnected || el.disabled || el.closest('[inert]')) return false
  if (el.checkVisibility)
    return el.checkVisibility({ visibilityProperty: true })
  return el.getClientRects().length > 0
}

// Tab order is DOM order here; positive tabindex isn't used in the app
export function focusableElements(container) {
  const radioGroups = new Map()
  const elements = [...container.querySelectorAll(TABBABLE)].filter((el) => {
    if (el.getAttribute('tabindex') === '-1' || !isFocusable(el)) return false
    if (el.type === 'radio' && el.name) {
      if (!radioGroups.has(el.name)) radioGroups.set(el.name, [])
      radioGroups.get(el.name).push(el)
    }
    return true
  })
  // Tab stops once per radio group: on the checked radio, else the first one (the last one going backwards)
  return elements.filter((el) => {
    const group = el.type === 'radio' && el.name && radioGroups.get(el.name)
    if (!group || group.length === 1) return true
    const checked = group.find((radio) => radio.checked)
    return checked ? el === checked : el === group[0] || el === group.at(-1)
  })
}

// Node.DOCUMENT_POSITION_* (where the active element sits relative to a candidate), spelled out
// so this module also runs under node --test, which has no Node global
const PRECEDING = 2
const FOLLOWING = 4

function sameRadioGroup(el, active) {
  return (
    el.type === 'radio' &&
    active.type === 'radio' &&
    el.name &&
    el.name === active.name &&
    el.form === active.form
  )
}

function focus(el) {
  el.focus({ preventScroll: true })
}

function focusContainer(container) {
  if (!container.hasAttribute('tabindex'))
    container.setAttribute('tabindex', '-1')
  focus(container)
}

export function focusLost(active) {
  return !active || active === document.body
}

// Moves focus into the container unless it's already there: the first focusable element (when
// `first`), otherwise the container itself.
export function focusInto(container, { first = true } = {}) {
  if (container.contains(document.activeElement)) return

  const target = first && focusableElements(container)[0]
  target ? focus(target) : focusContainer(container)
}

// Tab handler for the topmost layer. Acts only when focus is inside it, lost (e.g. its element was
// ajax-replaced), or still on `opener` while the layer animates in — never pulls focus back from
// somewhere else. Wraps only at the ends, so the browser handles every Tab in between.
export function wrapTab(event, container, { opener = null } = {}) {
  const active = document.activeElement
  const inside = container.contains(active)
  if (!inside && !focusLost(active) && active !== opener) return

  const elements = focusableElements(container)
  const backwards = event.shiftKey
  let target
  if (!inside) {
    target = backwards ? elements.at(-1) : elements[0]
  } else {
    // Stops left in the Tab direction. The browser moves out of a radio group in one step, so its
    // other radios don't count; the container itself precedes everything inside it.
    const ahead = elements.filter(
      (el) =>
        !sameRadioGroup(el, active) &&
        el.compareDocumentPosition(active) & (backwards ? FOLLOWING : PRECEDING)
    )
    if (ahead.length > 0) return
    target = backwards ? elements.at(-1) : elements[0]
  }

  event.preventDefault()
  target ? focus(target) : focusContainer(container)
}
